// src/components/ChecklistManager.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  orderBy,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI (Invariati) ---
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EventIcon from '@mui/icons-material/Event';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';

function ChecklistManager({ teamId }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('checklist');
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isSubmittingTemplate, setIsSubmittingTemplate] = useState(false);
  const [newItemText, setNewItemText] = useState({});
  const [isSubmittingItem, setIsSubmittingItem] = useState({});
  const [currentTab, setCurrentTab] = useState('Event');
  const [newTemplateType, setNewTemplateType] = useState('Event');

  // useEffect (invariato)
  useEffect(() => {
    if (!teamId) return;
    setIsLoading(true);
    const templatesQuery = query(
      collection(db, "checklistTemplates"),
      where("teamId", "==", teamId),
      orderBy("name", "asc")
    );
    const unsubscribe = onSnapshot(templatesQuery, (querySnapshot) => {
      const templatesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTemplates(templatesList);
      setIsLoading(false);
    }, (error) => {
      console.error("Error loading checklist templates:", error.message);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [teamId]);

  // --- MODIFICA: Funzione Creazione (con t()) ---
  const handleAddTemplate = async (e) => {
    e.preventDefault();
    const name = newTemplateName.trim();
    if (!name) return;
    
    if (templates.some(t => 
        (t.type || 'Event') === newTemplateType && 
        t.name.toLowerCase() === name.toLowerCase()
    )) {
        alert(t('exists_error', { type: newTemplateType })); // Tradotto
        return;
    }

    setIsSubmittingTemplate(true);
    try {
      await addDoc(collection(db, "checklistTemplates"), {
        teamId: teamId,
        name: name,
        type: newTemplateType,
        items: [],
        createdAt: serverTimestamp()
      });
      setNewTemplateName('');
    } catch (error) {
      console.error("Error adding template:", error);
      alert(t('add_error', { message: error.message })); // Tradotto
    } finally {
      setIsSubmittingTemplate(false);
    }
  };
  // --- FINE MODIFICA ---

  // --- MODIFICA: Funzioni Handler (con t()) ---
  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm(t('delete_confirm'))) { // Tradotto
        try {
            await deleteDoc(doc(db, "checklistTemplates", templateId));
        } catch (error) {
            console.error("Error deleting template:", error);
            alert(t('delete_error', { message: error.message })); // Tradotto
        }
    }
  };

  const handleAddItem = async (templateId) => {
    const text = (newItemText[templateId] || '').trim();
    if (!text) return;
    setIsSubmittingItem(prev => ({ ...prev, [templateId]: true }));
    const templateRef = doc(db, "checklistTemplates", templateId);
    try {
      await updateDoc(templateRef, {
        items: arrayUnion(text)
      });
      setNewItemText(prev => ({ ...prev, [templateId]: '' }));
    } catch (error) {
      console.error("Error adding item:", error);
      alert(t('add_item_error', { message: error.message })); // Tradotto
    } finally {
      setIsSubmittingItem(prev => ({ ...prev, [templateId]: false }));
    }
  };

  const handleRemoveItem = async (templateId, itemText) => {
    const templateRef = doc(db, "checklistTemplates", templateId);
    try {
        await updateDoc(templateRef, {
            items: arrayRemove(itemText)
        });
    } catch (error) {
        console.error("Error removing item:", error);
        alert(t('remove_item_error', { message: error.message })); // Tradotto
    }
  };
  // --- FINE MODIFICA ---

  // Filtro per Tab (invariato)
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      const type = t.type || 'Event'; 
      return type === currentTab;
    });
  }, [templates, currentTab]);


  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')} {/* Tradotto */}
        </Typography>
      </Paper>

      <Paper 
        elevation={0} 
        sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
      >
        <Box component="form" onSubmit={handleAddTemplate} sx={{ mb: 1 }}>
          <Typography variant="h5" gutterBottom>{t('add_title')}</Typography>
          
          <ToggleButtonGroup
            color="primary"
            value={newTemplateType}
            exclusive
            onChange={(e, newType) => newType && setNewTemplateType(newType)}
            aria-label={t('type_label')} // Tradotto
            sx={{ mb: 2.5 }}
          >
            <ToggleButton value="Event"><EventIcon sx={{ mr: 1 }} /> {t('type_event')}</ToggleButton>
            <ToggleButton value="Workshop"><HomeRepairServiceIcon sx={{ mr: 1 }} /> {t('type_workshop')}</ToggleButton>
          </ToggleButtonGroup>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'center' }}>
            <TextField
              label={t('name_label', { type: newTemplateType })} // Tradotto
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              required
              disabled={isSubmittingTemplate}
              sx={{ flexGrow: 1, width: '100%' }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmittingTemplate || !newTemplateName} // Aggiunto !newTemplateName
              startIcon={isSubmittingTemplate ? <CircularProgress size={20} /> : <AddIcon />}
              size="large"
              sx={{ height: '56px', width: { xs: '100%', md: 'auto' } }}
            >
              {t('create_button')} {/* Tradotto */}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper 
        elevation={0} 
        sx={{ border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="Template types">
            <Tab 
              label={t('tab_event')}
              value="Event" 
              icon={<EventIcon />} 
              iconPosition="start" 
            />
            <Tab 
              label={t('tab_workshop')}
              value="Workshop" 
              icon={<HomeRepairServiceIcon />} 
              iconPosition="start" 
            />
          </Tabs>
        </Box>
        
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {isLoading && <CircularProgress size={24} />}
          {!isLoading && filteredTemplates.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 2 }}>
              {t('empty', { tab: currentTab })} {/* Tradotto */}
            </Typography>
          )}

          {!isLoading && filteredTemplates.map((template) => (
              <Accordion 
                key={template.id} 
                sx={{ 
                  mb: 1, 
                  elevation: 0, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  backgroundColor: 'background.default',
                  '&:before': { display: 'none' } 
                }}
                disableGutters
              >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ flexGrow: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                    >
                         <PlaylistAddCheckIcon sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography sx={{ fontWeight: 'medium' }}>{template.name}</Typography>
                    </AccordionSummary>
                    <IconButton
                        aria-label="delete template"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleDeleteTemplate(template.id); 
                        }}
                        sx={{ mr: 1.5 }}
                    >
                        <DeleteIcon color="error" />
                    </IconButton>
                  </Box>
                  
                  <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
                      <Typography variant="subtitle2" gutterBottom>{t('items_title')}</Typography>
                      {template.items && template.items.length > 0 ? (
                          <List dense disablePadding>
                              {template.items.map((item, index) => (
                                  <ListItem
                                      key={index}
                                      disableGutters
                                      secondaryAction={
                                          <IconButton edge="end" size="small" aria-label="remove item" onClick={() => handleRemoveItem(template.id, item)}>
                                              <RemoveCircleOutlineIcon fontSize="small" />
                                          </IconButton>
                                      }
                                  >
                                      <ListItemText primary={`- ${item}`} />
                                  </ListItem>
                              ))}
                          </List>
                      ) : (
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('items_empty')}</Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
                         <TextField
                             label={t('new_item_label')}
                             size="small"
                             value={newItemText[template.id] || ''}
                             onChange={(e) => setNewItemText(prev => ({ ...prev, [template.id]: e.target.value }))}
                             disabled={isSubmittingItem[template.id]}
                             sx={{ flexGrow: 1 }}
                         />
                         <Button
                             size="small"
                             variant="outlined"
                             onClick={() => handleAddItem(template.id)}
                             disabled={isSubmittingItem[template.id]}
                             startIcon={isSubmittingItem[template.id] ? <CircularProgress size={16} /> : <AddIcon />}
                         >
                             {t('new_item_button')}
                         </Button>
                      </Box>
                  </AccordionDetails>
              </Accordion>
          ))}
        </Box>
      </Paper>
    </>
  );
}

export default ChecklistManager;